import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import globalUserProfile from '../../../../stores/global-user-profile';
import { CourseWithLectures } from '../../../../api/types/lecturers/courses/lecturer-courses-data-db';
import { ProfessorProfile } from '../../../../api/types/settings/settings-all';
import { UserType } from '../../../../api/types/user/user-type';
import { CourseStatus } from '../../../../api/types/programs/course/course-status';
import dateFormat from '../../../../utils/date-format';
import ProgramCoursePage from '../../../../pages/course/program-course-page';

export const MyCourses = observer(() => {
  const [selectedCourse, setSelectedCourse] =
    useState<CourseWithLectures | null>(null);
  // Get the professor's courses from the global user profile
  const getCourses = (): CourseWithLectures[] => {
    if (!globalUserProfile.userProfile) return [];

    if (globalUserProfile.userProfile.userType === UserType.LECTURER) {
      const professorProfile = globalUserProfile.userProfile
        .profile as ProfessorProfile;
      return professorProfile.courses || [];
    }

    return [];
  };

  const courses = getCourses();

  // Template for displaying course status
  const statusTemplate = (rowData: CourseWithLectures) => {
    const status = CourseStatus.parseCourseStatus(
      rowData.courseStatus?.toString() || 'INIT',
    );
    return (
      <span
        style={{
          padding: '4px 8px',
          borderRadius: '4px',
          fontSize: '12px',
          fontWeight: 'bold',
          backgroundColor: getStatusColor(status),
          color: 'white',
        }}
      >
        {CourseStatus.getDisplayText(status)}
      </span>
    );
  };

  // Get color for course status
  const getStatusColor = (status: CourseStatus): string => {
    switch (status) {
      case CourseStatus.ACTIVE:
        return '#4CAF50'; // Green
      case CourseStatus.FINISHED:
        return '#9E9E9E'; // Gray
      case CourseStatus.CLOSED:
        return '#F44336'; // Red
      case CourseStatus.INIT:
        return '#FF9800'; // Orange
      default:
        return '#9E9E9E';
    }
  };

  // Template for displaying course start date
  const dateTemplate = (rowData: CourseWithLectures) => {
    if (!rowData.courseStartDate) return '-';
    return dateFormat.displayFormatDate(new Date(rowData.courseStartDate));
  };

  // Template for displaying created date
  const createdTemplate = (rowData: CourseWithLectures) => {
    return dateFormat.displayFormatDate(new Date(rowData.courseCreatedAt));
  };

  // Template for displaying attendance percentage
  const attendanceTemplate = (rowData: CourseWithLectures) => {
    return `${rowData.attendancePercentage.toFixed(1)}%`;
  };

  // Template for displaying program info
  const programTemplate = (rowData: CourseWithLectures) => {
    return `${rowData.programName} (${rowData.programYears} שנים)`;
  };

  // Template for displaying lectures count
  const lecturesCountTemplate = (rowData: CourseWithLectures) => {
    return rowData.lectures ? rowData.lectures.length : 0;
  };

  // Handle row click to open course details
  const handleRowClick = (event: any) => {
    setSelectedCourse(event.data);
  };

  return (
    <div style={{ paddingTop: '20px', width: '100%' }}>
      <div
        style={{
          color: '#041E49',
          fontSize: '30px',
          fontWeight: 'bold',
          marginBottom: '20px',
        }}
      >
        הקורסים שלי
      </div>

      <DataTable
        value={courses}
        emptyMessage={<div style={{ textAlign: 'right' }}>לא נמצאו קורסים</div>}
        rowHover
        dataKey="courseId"
        scrollable
        scrollHeight="calc(100vh - 200px)"
        tableStyle={{
          direction: 'rtl',
        }}
        className="p-datatable-striped"
        onRowClick={handleRowClick}
        selectionMode="single"
      >
        <Column
          field="courseName"
          header="שם הקורס"
          style={{ textAlign: 'right', minWidth: '200px' }}
          headerStyle={{ textAlign: 'right' }}
        />

        <Column
          field="courseStatus"
          header="סטטוס"
          body={statusTemplate}
          style={{ textAlign: 'right', minWidth: '120px' }}
          headerStyle={{ textAlign: 'right' }}
        />

        <Column
          field="capacityHours"
          header="שעות קורס"
          style={{ textAlign: 'right', minWidth: '100px' }}
          headerStyle={{ textAlign: 'right' }}
        />

        <Column
          field="attendancePercentage"
          header="אחוז נוכחות"
          body={attendanceTemplate}
          style={{ textAlign: 'right', minWidth: '120px' }}
          headerStyle={{ textAlign: 'right' }}
        />

        <Column
          field="lecturesCount"
          header="מספר הרצאות"
          body={lecturesCountTemplate}
          style={{ textAlign: 'right', minWidth: '120px' }}
          headerStyle={{ textAlign: 'right' }}
        />

        <Column
          field="courseStartDate"
          header="תאריך התחלה"
          body={dateTemplate}
          style={{ textAlign: 'right', minWidth: '120px' }}
          headerStyle={{ textAlign: 'right' }}
        />
      </DataTable>

      {/* Course Details Dialog */}
      <Dialog
        header={selectedCourse ? `${selectedCourse.courseName}` : 'פרטי קורס'}
        visible={selectedCourse !== null}
        style={{
          width: '95vw',
          height: '90vh',
          backgroundColor: '#eeeeee',
        }}
        contentStyle={{
          backgroundColor: '#eeeeee',
        }}
        onHide={() => setSelectedCourse(null)}
        dismissableMask
        draggable={false}
        resizable={false}
        maximizable
      >
        {selectedCourse && (
          <ProgramCoursePage
            branchId={selectedCourse.branchId}
            programId={selectedCourse.programId}
            courseId={selectedCourse.courseId}
            isProfessor={true}
          />
        )}
      </Dialog>
    </div>
  );
});
